import { useEffect, useRef, useState, forwardRef } from 'react';
import {
  Box,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Paper,
  Popper,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Divider,
  Chip,
  FormHelperText,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from 'lodash/debounce';

export function createDebouncedLoader({ loader, minChars = 0, delay = 300 }) {
  let resolveRef;

  const debounced = debounce(async args => {
    try {
      const result = await loader(args);
      resolveRef?.(result);
    } catch {
      resolveRef?.({ data: [], total: 0 });
    }
  }, delay);

  return ({ query, ...rest }) => {
    if (!query || query.length < minChars) {
      debounced.cancel();
      return Promise.resolve({ data: [], total: 0 });
    }

    return new Promise(resolve => {
      resolveRef = resolve;
      debounced({ query, ...rest });
    });
  };
}

const AdvancedAutoComplete = forwardRef(
  (
    {
      label,
      defaultOption,
      loadOptions,
      pageSize = 10,
      minChars,
      onChange,
      placeholder,
      disabled = false,
      hasPagination = false,
      labelkey,
      valueKey,
      error = null,
      helperText = null,
      idKey = 'id',
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();

    const EMPTY_VALUE = {
      [labelkey]: '',
      [valueKey]: null,
    };

    const committedValueRef = useRef(EMPTY_VALUE);
    const isInitializedRef = useRef(false);
    const hasValidSelectionRef = useRef(false);

    const anchorRef = useRef(null);
    const inputRef = useRef(null);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [options, setOptions] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const minLength = minChars - 1;

    const load = async currentQuery => {
      if (currentQuery.length <= minLength) return;

      setLoading(true);
      try {
        const res = await loadOptions({
          query: currentQuery,
          page,
          pageSize,
        });

        setOptions(res?.data ?? []);

        if (hasPagination) {
          setTotalPages(res.total ?? 1);
        }
      } finally {
        setLoading(false);
      }
    };

    const onClear = () => {
      setQuery('');
      setOpen(false);
      onChange?.(EMPTY_VALUE);
      inputRef.current?.focus();
    };

    const handleSelect = option => {
      hasValidSelectionRef.current = true;
      setQuery(option[labelkey]);
      setOpen(false);
      onChange?.(option);
    };

    useEffect(() => {
      if (!isInitializedRef.current) return;
      if (query.length <= minLength) return;

      load(query);
    }, [query, page, pageSize]);

    useEffect(() => {
      if (props?.value?.[valueKey]) {
        setQuery(props.value[labelkey] ?? '');
      }
    }, [props?.value?.[labelkey]]);

    useEffect(() => {
      if (query.length <= minLength) {
        setOptions([]);
        setTotalPages(1);
      }
    }, [query]);

    useEffect(() => {
      if (isInitializedRef.current) return;

      if (defaultOption?.[valueKey]) {
        committedValueRef.current = defaultOption;
        setQuery(defaultOption[labelkey]);
        isInitializedRef.current = true;
      }
    }, [defaultOption]);

    const showClear = Boolean(query) && !disabled && !loading;

    const prev = committedValueRef.current;

    return (
      <Box ref={ref}>
        <TextField
          id={props?.name}
          inputRef={inputRef}
          size="small"
          ref={anchorRef}
          fullWidth
          disabled={disabled}
          label={label}
          placeholder={placeholder}
          value={query}
          error={Boolean(error)}
          onChange={e => {
            hasValidSelectionRef.current = false;
            isInitializedRef.current = true;
            setQuery(e.target.value);
            setPage(1);
            setOpen(true);
          }}
          InputProps={{
            ...props?.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                {loading && <CircularProgress size={18} />}
                {showClear && (
                  <IconButton
                    size="small"
                    aria-label="clear"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClear?.();
                    }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                {props?.InputProps?.endAdornment}
              </InputAdornment>
            ),
          }}
        />
        {error?.message || helperText ? (
          <FormHelperText sx={{ typography: 'body2' }} error={Boolean(error)}>
            {error?.message || helperText}
          </FormHelperText>
        ) : null}
        <ClickAwayListener
          disableReactTree
          onClickAway={() => {
            setOpen(false);
            if (!query.trim() && prev?.[valueKey]) {
              setQuery(prev[labelkey]);
              onChange(prev);
            }
            if (!hasValidSelectionRef.current) {
              setQuery(prev[labelkey] ?? '');
              onChange(prev);
            }
          }}>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{
              zIndex: 1300,
              marginTop: '10px',
              width: anchorRef.current?.offsetWidth,
            }}>
            <Paper elevation={6} sx={{ py: 1 }}>
              <List dense disablePadding>
                {!loading && query?.length <= minLength && (
                  <ListItemText
                    primary={`Please type in minimum ${minChars} characters`}
                    sx={{ px: 1, py: 0.5, color: theme.palette.primary.dark }}
                  />
                )}
                {prev?.[valueKey] && prev[labelkey] !== query && (
                  <ListItemButton
                    selected
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.info.light,
                        color: theme.palette.common.white,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: theme.palette.info.light,
                        color: theme.palette.common.white,
                      },
                    }}
                    key={prev[valueKey]}
                    onClick={() => handleSelect(prev)}>
                    <ListItemText
                      primary={`${prev[labelkey]} (Current saved value)`}
                    />
                  </ListItemButton>
                )}
                {!loading &&
                  query?.length > minLength &&
                  options.length === 0 && (
                    <ListItemText
                      primary="No results"
                      sx={{ px: 1, py: 0.5, color: theme.palette.primary.dark }}
                    />
                  )}
                {options?.length > 0 && (
                  <Divider
                    sx={{
                      m: 0,
                      mt: 1,
                      color: theme.palette.primary.main,
                    }}>
                    <Chip label="Search Results" size="small" />
                  </Divider>
                )}
                {options.map(option => (
                  <ListItemButton
                    key={option[idKey]}
                    onClick={() => handleSelect(option)}>
                    <ListItemText primary={option[labelkey]} />
                  </ListItemButton>
                ))}
              </List>

              {hasPagination && totalPages > 1 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  py={1}
                  borderTop="1px solid"
                  borderColor="divider">
                  <Pagination
                    size="small"
                    count={totalPages}
                    page={page}
                    onChange={(_, paginationPageValue) =>
                      setPage(paginationPageValue)
                    }
                  />
                </Box>
              )}
            </Paper>
          </Popper>
        </ClickAwayListener>
      </Box>
    );
  },
);

export default AdvancedAutoComplete;
